import React from 'react';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

// ─── Styling Helpers ─────────────────────────────────────────────────────────
// Sovereign Emerald register — cream surfaces, racing-green ink, champagne gold.
// EPC accent is champagne (not orange). AMS uses muted plum.

export type DeptMetaEntry = {
  label: string;
  /** Tailwind text class (used where Tailwind is convenient) */
  color: string;
  /** Tailwind bg class */
  bg: string;
  /** Tailwind border class */
  border: string;
  /** Tailwind dot bg class */
  dot: string;
  /** Tailwind pill bg+text (active tab badge) */
  tabColor: string;
  /** Inline style overrides for precise Sovereign token colours */
  style: { color: string; background: string; borderColor: string; dotColor: string };
};

export const DEPT_META: Record<string, DeptMetaEntry> = {
  // COMMON — muted steel-blue on cream
  COMMON: {
    label: 'Common',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
    tabColor: 'bg-blue-600 text-white',
    style: {
      color: '#3F6B82',
      background: 'rgba(63,107,130,0.08)',
      borderColor: 'rgba(63,107,130,0.24)',
      dotColor: '#3F6B82',
    },
  },
  // OMS — bottle/brand green
  OMS: {
    label: 'OMS',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-600',
    tabColor: 'bg-emerald-700 text-white',
    style: {
      color: SOVEREIGN.bottle,
      background: 'rgba(31,90,71,0.07)',
      borderColor: 'rgba(31,90,71,0.22)',
      dotColor: SOVEREIGN.brand,
    },
  },
  // AMS — muted plum/amethyst
  AMS: {
    label: 'AMS',
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    dot: 'bg-purple-500',
    tabColor: 'bg-purple-700 text-white',
    style: {
      color: '#6B5A8A',
      background: 'rgba(107,90,138,0.08)',
      borderColor: 'rgba(107,90,138,0.22)',
      dotColor: '#6B5A8A',
    },
  },
  // EPC — champagne gold (NOT orange)
  EPC: {
    label: 'EPC',
    color: 'text-yellow-700',
    bg: 'bg-yellow-50',
    border: 'border-yellow-300',
    dot: 'bg-yellow-500',
    tabColor: 'bg-yellow-700 text-white',
    style: {
      color: '#8A5A08',
      background: 'rgba(201,169,97,0.10)',
      borderColor: 'rgba(201,169,97,0.32)',
      dotColor: SOVEREIGN.champagne,
    },
  },
};

export const getDeptMeta = (dept: string): DeptMetaEntry =>
  DEPT_META[dept] ?? DEPT_META['COMMON'];

export function DeptBadge({ dept, label }: { dept: string; label?: string }) {
  const meta = getDeptMeta(dept);
  const display = label ?? dept;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{
        color: meta.style.color,
        background: meta.style.background,
        border: `1px solid ${meta.style.borderColor}`,
      }}
      title={display}
    >
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ background: meta.style.dotColor }}
        aria-hidden
      />
      {display}
    </span>
  );
}

export default DeptBadge;
